import React, { useState, useEffect } from 'react';
import { homeworkService } from '../../lib/services/homework';
import ClassManagement from './ClassManagement';
import TeacherManagement from './TeacherManagement';
import StudentManagement from './StudentManagement';
import TimetableSystem from './TimetableSystem';
import HomeworkCalendar from './HomeworkCalendar';
import SearchFilter from './SearchFilter';
import StatisticsPanel from './StatisticsPanel';
import './HomeworkModule.css';

const HomeworkModule = ({ currentUser, onBack, onTabChange }) => {
  const [currentView, setCurrentView] = useState('classes'); // 'classes', 'teachers', 'students'
  const [selectedItem, setSelectedItem] = useState(null);
  const [showTimetable, setShowTimetable] = useState(false);
  const [showHomeworkCalendar, setShowHomeworkCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [filteredTeachers, setFilteredTeachers] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({});

  // Time slots and day names
  const timeSlots = [
    '8:10-8:45', '8:45-9:20', '9:20-9:55', '10:15-10:50', 
    '10:50-11:25', '11:30-12:05', '12:05-12:40', '1:50-2:25', 
    '2:25-3:00', '3:00-3:35'
  ];

  const dayNames = ['星期一', '星期二', '星期三', '星期四', '星期五'];
  const dayKeys = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [classes, teachers, students, statistics] = await Promise.all([
        homeworkService.getClasses(),
        homeworkService.getTeachers(),
        homeworkService.getStudents(),
        homeworkService.getHomeworkStats()
      ]);

      setFilteredClasses(classes);
      setFilteredTeachers(teachers);
      setFilteredStudents(students);
      setStats(statistics);
    } catch (err) {
      console.error('Error loading homework data:', err);
      setError('載入家課管理資料時發生錯誤');
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setShowTimetable(true);
    setShowHomeworkCalendar(false);
  };

  const handleTimetableClick = (timeSlot, day) => {
    if (selectedItem) {
      setSelectedDate(new Date().toISOString().split('T')[0]);
      setShowHomeworkCalendar(true);
    }
  };

  const handleBackToList = () => {
    setShowTimetable(false);
    setShowHomeworkCalendar(false);
    setSelectedItem(null);
    setSelectedDate(null);
  };

  const handleBackToTimetable = () => {
    setShowHomeworkCalendar(false);
    setSelectedDate(null);
  };

  if (loading) {
    return (
      <div className="homework-module">
        <div className="homework-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>家課管理系統</h1>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>載入家課管理中...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="homework-module">
        <div className="homework-header">
          <button className="back-btn" onClick={onBack}>
            ← 返回主頁
          </button>
          <h1>家課管理系統</h1>
        </div>
        <div className="error-container">
          <p>❌ {error}</p>
          <button onClick={loadInitialData} className="retry-btn">
            重新載入
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="homework-module">
      <div className="homework-header">
        <button className="back-btn" onClick={onBack}>
          ← 返回主頁
        </button>
        <h1>家課管理系統</h1>
        {!showTimetable && !showHomeworkCalendar && (
          <div className="homework-nav">
            <button 
              className={`nav-btn ${currentView === 'classes' ? 'active' : ''}`}
              onClick={() => setCurrentView('classes')}
            >
              🏛️ 班級管理
            </button>
            <button 
              className={`nav-btn ${currentView === 'teachers' ? 'active' : ''}`}
              onClick={() => setCurrentView('teachers')}
            >
              👨‍🏫 教師管理
            </button>
            <button 
              className={`nav-btn ${currentView === 'students' ? 'active' : ''}`}
              onClick={() => setCurrentView('students')}
            >
              👨‍🎓 學生管理
            </button>
          </div>
        )}
      </div>

      <div className="homework-content">
        {!showTimetable && !showHomeworkCalendar && (
          <>
            <StatisticsPanel stats={stats} />
            
            <SearchFilter
              data={currentView === 'classes' ? filteredClasses : 
                    currentView === 'teachers' ? filteredTeachers : filteredStudents}
              onFilter={currentView === 'classes' ? setFilteredClasses : 
                       currentView === 'teachers' ? setFilteredTeachers : setFilteredStudents}
              type={currentView.slice(0, -1)} // Remove 's' from end
              placeholder={`搜尋${currentView === 'classes' ? '班級' : 
                          currentView === 'teachers' ? '教師' : '學生'}...`}
            />

            {currentView === 'classes' && (
              <ClassManagement 
                classes={filteredClasses}
                onClassSelect={handleItemSelect}
              />
            )}

            {currentView === 'teachers' && (
              <TeacherManagement 
                teachers={filteredTeachers}
                onTeacherSelect={handleItemSelect}
              />
            )}

            {currentView === 'students' && (
              <StudentManagement 
                students={filteredStudents}
                onStudentSelect={handleItemSelect}
              />
            )}
          </>
        )}

        {showTimetable && !showHomeworkCalendar && (
          <TimetableSystem
            selectedItem={selectedItem}
            timeSlots={timeSlots}
            dayNames={dayNames}
            dayKeys={dayKeys}
            onTimetableClick={handleTimetableClick}
            onBack={handleBackToList}
          />
        )}

        {showHomeworkCalendar && (
          <HomeworkCalendar
            selectedItem={selectedItem}
            selectedDate={selectedDate}
            onBack={handleBackToTimetable}
          />
        )}
      </div>
    </div>
  );
};

export default HomeworkModule;
